import type { FetchResult } from '@apollo/client';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useMemo, useRef, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { ListItemToggle } from '@components/ListItem';
import {
  createPatientPlansSchema,
  getEligibleFeatures,
  sumAllocatedSeats,
} from '@components/PatientPlans';
import RoundedButton from '@components/RoundedButton';
import ViewTab from '@components/ViewTab/ViewTab';
import type { TabValue } from '@components/ViewTab/ViewTab';

import {
  useAccountSettings,
  useUpdateAccountSettings,
} from '@hooks/api/account-settings';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSession } from '@hooks/useSession';

import type {
  CustomPlanFieldsFragment,
  OrgFeatureChangeFieldsFragment,
} from 'generated/custom-plan.generated';
import {
  useGetPatientSeatSummary,
  useListCustomPlans,
  useListOrgFeatures,
  usePreviewOrgFeatureChangeLazyQuery,
  useSaveCustomPlans,
  useUpdateOrgFeatures,
  useUpdatePatientSeats,
} from 'generated/custom-plan.generated';
import { useGetAccountSetting } from 'generated/license.generated';
import { useListPurchasedPlans } from 'generated/plan.generated';
import type { CustomPlanInput } from 'generated/types';

import LicenseCard from './components/Card';
import FeatureChangeDialog from './components/FeatureChangeDialog';
import OrgFeaturesTab from './components/OrgFeaturesTab';
import PatientPlansTab from './components/PatientPlansTab';
import type { LicensePlanValues } from './types';

const messages = defineMessages({
  medlyTitle: {
    id: 'organization.setting.licensePlan.medlyTitle',
    defaultMessage: 'Enable Medly for organization',
  },
  medlySubtitle: {
    id: 'organization.setting.licensePlan.medlySubtitle',
    defaultMessage: 'Include Medly as an add-on',
  },
  orgFeatures: {
    id: 'orgSettings.licensePlan.orgFeatures',
    defaultMessage: 'Organization Features',
  },
  patientPlans: {
    id: 'patientPlanTable.title',
    defaultMessage: 'Patient Plans',
  },
  save: {
    id: 'GLOBAL.saveChanges',
    defaultMessage: 'Save Changes',
  },
  required: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  seatsInteger: {
    id: 'newAccount.patientSeats.integer',
    defaultMessage: 'Patient seats must be a whole number',
  },
  seatsMin: {
    id: 'newAccount.patientSeats.min',
    defaultMessage: 'Patient seats must be greater than 0',
  },
  seatsBelowAllocated: {
    id: 'orgSettings.licensePlan.seatsBelowAllocated',
    defaultMessage:
      'Patient plans already allocate {allocated} seats. Raise patient seats to at least {allocated} to save.',
  },
  seatPoolExceeded: {
    id: 'orgSettings.licensePlan.seatPoolExceeded',
    defaultMessage:
      'Plans allocate {allocated} seats, more than the {total} in the organization’s pool.',
  },
  previewFailed: {
    id: 'orgSettings.licensePlan.previewFailed',
    defaultMessage:
      'Could not check which patient plans use these features. Nothing was saved — please try again.',
  },
  partialSave: {
    id: 'orgSettings.licensePlan.partialSave',
    defaultMessage:
      'Only part of your changes were saved ({saved}). The rest were not applied — please try again.',
  },
  partSeats: {
    id: 'orgSettings.licensePlan.part.seats',
    defaultMessage: 'patient seats',
  },
  partFeatures: {
    id: 'orgSettings.licensePlan.part.features',
    defaultMessage: 'organization features',
  },
  partPlans: {
    id: 'orgSettings.licensePlan.part.plans',
    defaultMessage: 'patient plans',
  },
});

/**
 * No seat pool is configured yet, so the plans cannot over-allocate it;
 * validating against 0 would flag every existing plan.
 */
const NO_SEAT_LIMIT = Number.MAX_SAFE_INTEGER;

type TabId = 'org-features' | 'patient-plans';
type SavedPart = 'features' | 'plans' | 'seats';

/** Shapes a form plan for `saveCustomPlans`, dropping ineligible features. */
function toPlanInput(
  plan: Pick<
    LicensePlanValues['plans'][number],
    'description' | 'feature_ids' | 'name' | 'plan_id' | 'seats'
  >,
  /** Omit to leave `feature_ids` untouched — see the catalog-failure case. */
  eligibleIds?: ReadonlySet<string>
): CustomPlanInput {
  return {
    // Absent for a plan that does not exist server-side yet.
    ...(plan.plan_id ? { plan_id: plan.plan_id } : {}),
    name: plan.name.trim(),
    description: plan.description.trim() || null,
    seats: Number(plan.seats),
    feature_ids: eligibleIds
      ? plan.feature_ids.filter((id) => eligibleIds.has(id))
      : plan.feature_ids,
  };
}

interface LicensePlanProps {
  account_id: string;
}

function LicensePlan({ account_id }: LicensePlanProps) {
  const { formatMessage } = useIntl();
  const { flags } = useFeatureFlags();

  const tabs: readonly TabValue<TabId>[] = [
    { id: 'org-features', value: formatMessage(messages.orgFeatures) },
    { id: 'patient-plans', value: formatMessage(messages.patientPlans) },
  ];
  const [activeTab, setActiveTab] = useState<TabValue<TabId>>(tabs[0]);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [savedParts, setSavedParts] = useState<SavedPart[] | null>(null);
  const [pendingChanges, setPendingChanges] = useState<
    OrgFeatureChangeFieldsFragment[]
  >([]);
  // Lets the ordered save await the cascade dialog's outcome.
  const confirmResolver = useRef<((confirmed: boolean) => void) | null>(null);
  const {
    state: { isCurator, isAdmin },
  } = useSession();
  const canManageMedly = isCurator || isAdmin;

  const { data: licenseData, loading: licenseLoading } = useGetAccountSetting({
    variables: { input: { account_id } },
    skip: !account_id,
  });

  const {
    data: purchasedPlansData,
    loading: purchasedPlansLoading,
    refetch: refetchPurchasedPlans,
  } = useListPurchasedPlans({
    variables: { input: { account_id } },
    skip: !account_id,
  });

  const {
    data: orgFeaturesData,
    error: orgFeaturesError,
    loading: orgFeaturesLoading,
    refetch: refetchOrgFeatures,
  } = useListOrgFeatures({
    variables: { input: { account_id } },
    skip: !account_id,
  });

  const {
    data: customPlansData,
    loading: customPlansLoading,
    refetch: refetchCustomPlans,
  } = useListCustomPlans({
    variables: { input: { account_id } },
    skip: !account_id,
  });

  const {
    data: seatSummaryData,
    loading: seatSummaryLoading,
    refetch: refetchSeatSummary,
  } = useGetPatientSeatSummary({
    variables: { input: { account_id } },
    skip: !account_id,
  });

  const {
    accountSettings: accountSettingsData,
    loading: accountSettingsLoading,
  } = useAccountSettings(account_id);
  const [updateAccountSetting, updateAccountSettingLoading] =
    useUpdateAccountSettings();

  const [previewChange] = usePreviewOrgFeatureChangeLazyQuery();
  const [updateSeats] = useUpdatePatientSeats();
  const [updateOrgFeatures] = useUpdateOrgFeatures();
  const [saveCustomPlans] = useSaveCustomPlans();

  const loading =
    licenseLoading ||
    purchasedPlansLoading ||
    accountSettingsLoading ||
    customPlansLoading ||
    seatSummaryLoading;

  const medly_enabled = accountSettingsData?.getAccountSetting?.medly_enabled;
  const [isMedlyEnabled, setIsMedlyEnabled] = useState(medly_enabled);
  const handleMedlyEnabledToggle = (checked: boolean) => {
    updateAccountSetting({ account_id, medly_enabled: checked });
    setIsMedlyEnabled(checked);
  };

  // Memoized so the fallbacks keep a stable identity: a fresh `[]` each render
  // would rebuild `initialValues`, and `enableReinitialize` would reset the
  // form under the admin.
  const features = useMemo(
    () => orgFeaturesData?.listOrgFeatures ?? [],
    [orgFeaturesData]
  );
  const savedPlans: CustomPlanFieldsFragment[] = useMemo(
    () => customPlansData?.listCustomPlans ?? [],
    [customPlansData]
  );
  const purchasedPlans = useMemo(
    () => purchasedPlansData?.listPurchasedPlans ?? [],
    [purchasedPlansData]
  );
  const seatSummary = seatSummaryData?.getPatientSeatSummary;
  // An empty list is indistinguishable from "this org has no features", so a
  // failed fetch makes the page unsafe to save: `updateOrgFeatures` would write
  // an empty selection and the prune below would strip every plan's features.
  const featuresFailed = Boolean(orgFeaturesError);
  const canEditFeatures = !featuresFailed;

  const savedSeatTotal = seatSummary?.total ?? null;
  const allocated = seatSummary?.allocated ?? 0;

  const savedFeatureIds = useMemo(
    () =>
      features
        .filter(({ is_enabled }) => is_enabled)
        .map(({ feature_id }) => feature_id),
    [features]
  );

  const initialValues: LicensePlanValues = useMemo(() => {
    const eligibleIds = new Set(
      getEligibleFeatures(features, savedFeatureIds).map(
        ({ feature_id }) => feature_id
      )
    );
    const sorted = [...savedPlans].sort(
      (a, b) => a.display_order - b.display_order
    );

    return {
      license_id: licenseData?.getAccountSetting?.license_id ?? '',
      limit_patients: savedSeatTotal ?? '',
      org_feature_ids: savedFeatureIds,
      plans: sorted.map((plan) => ({
        // Derived from plan_id rather than minted, so reseeding produces the
        // same values and `enableReinitialize` leaves the form alone.
        local_id: plan.plan_id,
        plan_id: plan.plan_id,
        name: plan.name,
        description: plan.description ?? '',
        seats: plan.seats,
        assigned_seats: plan.assigned_seats,
        // Drop features disabled since the plan was saved, or they linger and
        // get re-submitted. Skipped when the catalog failed to load: an empty
        // catalog is not evidence they are gone.
        feature_ids: featuresFailed
          ? plan.feature_ids
          : plan.feature_ids.filter((id) => eligibleIds.has(id)),
      })),
    };
  }, [
    features,
    savedFeatureIds,
    savedPlans,
    savedSeatTotal,
    featuresFailed,
    licenseData,
  ]);

  // Lazy so the seat-pool test reads the count being entered rather than the
  // saved pool: raising the pool and allocating the new seats then validates in
  // one pass.
  const validationSchema = useMemo(
    () =>
      Yup.lazy((current: unknown) => {
        const form = current as LicensePlanValues | undefined;
        const entered = Number(form?.limit_patients);
        // No pool configured yet, so the plans cannot over-allocate it.
        // Validating against 0 would flag every existing plan instead.
        const totalSeats =
          Number.isFinite(entered) && entered > 0 ? entered : NO_SEAT_LIMIT;
        // The allocation being saved, not the one on record: plans are written
        // in the same action, so shrinking the plans and the pool together is
        // legal.
        const allocatedInForm = sumAllocatedSeats(form?.plans ?? []);

        return Yup.object().shape({
          // `license_id` is deliberately unvalidated: it is display-only and
          // never submitted, so requiring it would let a missing value disable
          // Save for the whole page.
          limit_patients: Yup.number()
            .typeError(formatMessage(messages.seatsInteger))
            .required(formatMessage(messages.required))
            .integer(formatMessage(messages.seatsInteger))
            .min(1, formatMessage(messages.seatsMin))
            // A named test rather than a second `.min()`: Yup's min is
            // exclusive and would silently replace the min-1 rule above.
            .test({
              name: 'seats-not-below-allocated',
              message: formatMessage(messages.seatsBelowAllocated, {
                allocated: allocatedInForm,
              }),
              // Leave the empty case to `.required()`.
              skipAbsent: true,
              test: (value) => value >= allocatedInForm,
            }),
          org_feature_ids: Yup.array()
            .of(Yup.string().required())
            .defined()
            .default([]),
          plans: createPatientPlansSchema(
            { formatMessage },
            {
              totalSeats,
              seatPoolMessage: (allocatedSeats, total) =>
                formatMessage(messages.seatPoolExceeded, {
                  allocated: allocatedSeats,
                  total,
                }),
            }
          ),
        });
      }),
    [formatMessage]
  );

  /**
   * Refetch only the slices that were actually written. Refetching everything
   * after a partial failure would reseed the form through `enableReinitialize`
   * and discard edits for the slices that did not save.
   */
  async function refetchApplied(parts: SavedPart[]) {
    const reads: Promise<unknown>[] = [];
    if (parts.includes('seats')) {
      reads.push(refetchSeatSummary());
    }
    if (parts.includes('features')) {
      reads.push(refetchOrgFeatures());
    }
    if (parts.includes('plans')) {
      // Plan writes move the Contract_Plan rows the status counts read from.
      reads.push(
        refetchCustomPlans(),
        refetchPurchasedPlans(),
        refetchSeatSummary()
      );
    }
    await Promise.all(reads);
  }

  /** Opens the cascade dialog and resolves once the admin answers. */
  function confirmFeatureRemoval(changes: OrgFeatureChangeFieldsFragment[]) {
    // A second prompt would orphan the first resolver, suspending that save
    // for good. Unreachable while Save is disabled mid-submit, but cheap.
    if (confirmResolver.current) {
      return Promise.resolve(false);
    }
    setPendingChanges(changes);
    return new Promise<boolean>((resolve) => {
      confirmResolver.current = resolve;
    });
  }

  function settleConfirm(confirmed: boolean) {
    setPendingChanges([]);
    confirmResolver.current?.(confirmed);
    confirmResolver.current = null;
  }

  // Unmounting mid-confirmation would leave `handleSave` awaiting forever.
  React.useEffect(
    () => () => {
      confirmResolver.current?.(false);
      confirmResolver.current = null;
    },
    []
  );

  async function handleSave(values: LicensePlanValues) {
    setPreviewFailed(false);
    setSavedParts(null);

    const nextSeats = Number(values.limit_patients);
    const seatsDirty = nextSeats !== savedSeatTotal;
    const featuresDirty =
      [...values.org_feature_ids].sort().join('|') !==
      [...savedFeatureIds].sort().join('|');

    // Disabling a feature strips it from every plan that includes it and
    // revokes live patient access, so that is gated before anything is written.
    if (featuresDirty) {
      let preview;
      try {
        const { data, error: previewError } = await previewChange({
          variables: {
            input: { account_id, feature_ids: values.org_feature_ids },
          },
        });
        preview = previewError ? undefined : data?.previewOrgFeatureChange;
      } catch {
        preview = undefined;
      }

      // Never fall through without a preview: an absent result is
      // indistinguishable from "no plans are affected", which would silently
      // strip features from the plans using them.
      if (!preview) {
        setPreviewFailed(true);
        return;
      }

      const usedByAPlan = preview.to_disable.filter(
        (change) => change.affected_plans.length > 0
      );
      if (usedByAPlan.length > 0) {
        const confirmed = await confirmFeatureRemoval(usedByAPlan);
        if (!confirmed) {
          return;
        }
      }
    }

    // Plans are validated server-side against the stored seat pool and enabled
    // features, so those land first. Pruning against a catalog that failed to
    // load would empty every plan, so it is skipped in that state.
    const eligibleIds = canEditFeatures
      ? new Set(
          getEligibleFeatures(features, values.org_feature_ids).map(
            ({ feature_id }) => feature_id
          )
        )
      : undefined;
    const nextPlans = values.plans.map((plan) =>
      toPlanInput(plan, eligibleIds)
    );
    const plansDirty =
      JSON.stringify(nextPlans) !==
      JSON.stringify(
        initialValues.plans.map((plan) => toPlanInput(plan, eligibleIds))
      );

    const steps: {
      part: SavedPart;
      run: () => Promise<FetchResult<unknown>>;
    }[] = [
      {
        part: 'seats',
        run: () =>
          updateSeats({
            variables: { input: { account_id, limit_patients: nextSeats } },
          }),
      },
      {
        part: 'features',
        run: () =>
          updateOrgFeatures({
            variables: {
              input: { account_id, feature_ids: values.org_feature_ids },
            },
          }),
      },
      {
        part: 'plans',
        run: () =>
          saveCustomPlans({
            variables: { input: { account_id, plans: nextPlans } },
          }),
      },
    ];

    const dirtyBy: Record<SavedPart, boolean> = {
      features: featuresDirty,
      plans: plansDirty,
      seats: seatsDirty,
    };

    // Order depends on which way the pool moves: raising it, plans validate
    // against the stored pool, so seats land first; lowering it, seats validate
    // against the stored allocation, so plans shrink first. Features always
    // precede plans, whose feature ids validate against the stored enabled set.
    const loweringPool = seatsDirty && nextSeats < allocated;
    const order: SavedPart[] = loweringPool
      ? ['features', 'plans', 'seats']
      : ['seats', 'features', 'plans'];

    const applied: SavedPart[] = [];
    try {
      for (const part of order) {
        if (!dirtyBy[part]) {
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const result = await steps.find((step) => step.part === part)?.run();
        // `mutate.errorPolicy` is 'all' (apollo.ts), so a rejection resolves
        // with { data, errors } rather than throwing. Without this check the
        // loop would run on and save plans referencing a feature that failed to
        // enable.
        if (result?.errors?.length) {
          throw new Error(`${part} was rejected`);
        }
        applied.push(part);
      }
    } catch {
      // Three sequential writes with no transaction. The wrapped hooks surface
      // the failure; name what did land so the admin knows the save was
      // partial.
      if (applied.length > 0) {
        setSavedParts(applied);
      }
      await refetchApplied(applied);
      return;
    }

    if (applied.length > 0) {
      // None of these models normalize into the cache (no id, no typePolicy),
      // so the mutation results cannot update the queries in place.
      await refetchApplied(applied);
    }
  }

  const partLabels: Record<SavedPart, string> = {
    features: formatMessage(messages.partFeatures),
    plans: formatMessage(messages.partPlans),
    seats: formatMessage(messages.partSeats),
  };

  return (
    <ContentLoader lineCount={5} loading={loading}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSave}
        validationSchema={validationSchema}
      >
        {({ dirty, isSubmitting, isValid, values }) => {
          const enteredSeats = Number(values.limit_patients);
          const poolTotal =
            values.limit_patients !== '' && Number.isFinite(enteredSeats)
              ? enteredSeats
              : savedSeatTotal;

          return (
            <Form>
              <Stack gap={2}>
                {canManageMedly && (
                  <ListItemToggle
                    disabled={
                      updateAccountSettingLoading ||
                      !flags.isAccountUpdateAccountSettingEnabled
                    }
                    enabled={isMedlyEnabled}
                    icon_name="heart"
                    onToggle={handleMedlyEnabledToggle}
                    primary_text={formatMessage(messages.medlyTitle)}
                    secondary_text={formatMessage(messages.medlySubtitle)}
                  />
                )}

                <LicenseCard
                  allocated={sumAllocatedSeats(values.plans)}
                  license={licenseData?.getAccountSetting}
                />

                <ViewTab
                  changeTab={setActiveTab}
                  selected={activeTab}
                  tabArray={tabs}
                />

                {/* One panel per tab, so each reads as a single card.
                    MuiDataTable paints only its table body, which would
                    leave the plans title and totals on the page background. */}
                <Card>
                  <CardContent>
                    {activeTab.id === 'org-features' ? (
                      <OrgFeaturesTab
                        error={featuresFailed}
                        features={features}
                        loading={orgFeaturesLoading}
                        onRetry={refetchOrgFeatures}
                      />
                    ) : (
                      <PatientPlansTab
                        features={features}
                        purchasedPlans={purchasedPlans}
                        totalSeats={poolTotal}
                      />
                    )}
                  </CardContent>
                </Card>

                {previewFailed && (
                  <Typography
                    color="error"
                    data-testid="license-plan-preview-error"
                    variant="body2"
                  >
                    {formatMessage(messages.previewFailed)}
                  </Typography>
                )}

                {savedParts && (
                  <Typography
                    color="error"
                    data-testid="license-plan-partial-save"
                    variant="body2"
                  >
                    {formatMessage(messages.partialSave, {
                      saved: savedParts
                        .map((part) => partLabels[part])
                        .join(', '),
                    })}
                  </Typography>
                )}

                <Box alignSelf="flex-end">
                  <RoundedButton
                    autoSize
                    disabled={
                      !dirty || !isValid || isSubmitting || !canEditFeatures
                    }
                    margin={0}
                    type="submit"
                  >
                    {formatMessage(messages.save)}
                  </RoundedButton>
                </Box>
              </Stack>

              <FeatureChangeDialog
                changes={pendingChanges}
                onCancel={() => settleConfirm(false)}
                onConfirm={async () => settleConfirm(true)}
              />
            </Form>
          );
        }}
      </Formik>
    </ContentLoader>
  );
}

export default withErrorBoundary(LicensePlan, {
  FallbackComponent: ErrorBoundaryFallback,
});
